import React from 'react';
import PropTypes from 'prop-types';


import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import InputAdornment from '@mui/material/InputAdornment';
import Tooltip from '@mui/material/Tooltip';
import Link from '@mui/material/Link';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import InfoIcon from '@mui/icons-material/Info';
import PersonIcon from '@mui/icons-material/Person';
import LockIcon from '@mui/icons-material/Lock';

import LoadingButton from 'components/Shared/LoadingButton';
import PasswordTextField from 'components/Shared/PasswordTextField';

class LoginForm extends React.Component {

    state = {
        passwordHasBeenFocused: false,
        forgotPasswordDialogOpen: false
    };

    handleKeyUp = (event) => {
        let { username, password } = this.props;

        if (event.key === 'Enter' && username && password ) {
            this.props.onSubmit();
        }
    }

    handlePasswordFocus = () => {
        this.setState({ passwordHasBeenFocused: true });
    }

    handleOpenForgotPassword = () => {
        this.setState({ forgotPasswordDialogOpen: true });
    }

    handleCloseForgotPassword = () => {
        this.setState({ forgotPasswordDialogOpen: false });
    }

    render() {
        // Détection du type d'utilisateur basé sur le format du nom d'utilisateur
        const isEmailFormat = this.props.username.includes('@');
        const isRepresentant = isEmailFormat;
        const showHelp = this.state.passwordHasBeenFocused && this.props.username.length > 0;

        const tooltipContent = (
            <Box>
                <Typography variant="body2" gutterBottom><strong>Représentant :</strong></Typography>
                <Typography variant="body2" sx={{ mb: 1.5 }}>
                    • Format email requis (xxx@foussier.fr)<br />
                    • Utilisez votre mot de passe ATC
                </Typography>
                <Typography variant="body2" gutterBottom><strong>Autres :</strong></Typography>
                <Typography variant="body2">
                    • Login Active Directory (sans @)<br />
                    • Utilisez votre mot de passe Windows
                </Typography>
            </Box>
        );

        return (
            <form onSubmit={(e) => e.preventDefault()}>
                <Grid container spacing={3} onKeyUp={this.handleKeyUp}>
                    <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary" align="center">
                            Identifiez-vous pour accéder au portail après-vente !
                        </Typography>
                    </Grid>

                    <Grid container item xs={12} justifyContent="center">
                        <TextField
                            id="username"
                            label="Identifiant"
                            fullWidth
                            value={this.props.username}
                            onChange={this.props.onChange}
                            onKeyUp={this.handleKeyUp}
                            inputProps={{
                                id: 'username',
                                name: 'username'
                            }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <PersonIcon />
                                    </InputAdornment>
                                ),
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <Tooltip title={tooltipContent} arrow placement="right">
                                            <InfoIcon color="info" sx={{ cursor: 'help' }} />
                                        </Tooltip>
                                    </InputAdornment>
                                ),
                            }}
                            autoFocus
                        />
                    </Grid>

                    <Grid container item xs={12} justifyContent="center">
                        <PasswordTextField
                            id="password"
                            label="Mot de passe"
                            fullWidth
                            value={this.props.password}
                            onChange={this.props.onChange}
                            onKeyUp={this.handleKeyUp}
                            onFocus={this.handlePasswordFocus}
                            startAdornment={
                                <InputAdornment position="start">
                                    <LockIcon />
                                </InputAdornment>
                            }
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Box sx={{ visibility: showHelp ? 'visible' : 'hidden', minHeight: '72px' }}>
                            {isRepresentant ? (
                                <Alert severity="info" sx={{ fontSize: '0.875rem' }}>
                                    <strong>Mode représentant détecté</strong><br />
                                    Utilisez votre mot de passe ATC (idem Web/Easy).
                                </Alert>
                            ) : (
                                <Alert severity="info" sx={{ fontSize: '0.875rem' }}>
                                    <strong>Mode autre que représentant détecté</strong><br />
                                    Utilisez votre mot de passe Windows.
                                </Alert>
                            )}
                        </Box>
                    </Grid>

                    <Grid container item xs={12} justifyContent="center">
                        <LoadingButton
                            disabled={this.props.isLoading || !this.props.username || !this.props.password}
                            onClick={this.props.onSubmit}
                            label="Se connecter"
                            pending={this.props.isLoading}
                            fullWidth
                        />
                    </Grid>

                    <Grid container item xs={12} justifyContent="center">
                        <Link
                            component="button"
                            type="button"
                            variant="body2"
                            onClick={this.handleOpenForgotPassword}
                            sx={(theme) => ({
                                ...theme.style.link,
                                cursor: 'pointer',
                                mt: 1,
                                border: 'none',
                                background: 'none',
                                padding: 0,
                                font: 'inherit'
                            })}
                        >
                            Mot de passe oublié ?
                        </Link>
                    </Grid>
                </Grid>

                <Dialog open={this.state.forgotPasswordDialogOpen} onClose={this.handleCloseForgotPassword} maxWidth="sm" fullWidth>
                    <DialogTitle>Mot de passe oublié</DialogTitle>
                    <DialogContent>
                        <Typography variant="body1" paragraph>
                            Vous avez oublié votre mot de passe et n'êtes plus en mesure de vous connecter ?
                        </Typography>
                        <Typography variant="body1">
                            Merci de nous contacter à l'adresse : <Link href="mailto:support.informatique@foussier.fr">support.informatique@foussier.fr</Link>
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={this.handleCloseForgotPassword} variant="contained" color="primary">
                            RETOUR À LA PAGE DE CONNEXION
                        </Button>
                    </DialogActions>
                </Dialog>
            </form>
        );
    }
}

LoginForm.propTypes = {
    isLoading: PropTypes.bool.isRequired,

    onChange: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,

    username: PropTypes.string.isRequired,
    password: PropTypes.string.isRequired,
    domaine: PropTypes.number.isRequired
};

export default LoginForm;
